import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { getAccessTokenForScope } from '../services/AuthClient';
import { appConfiguration } from '../../environments/app.config';

// ---------- Types ----------
export interface Tenant {
  id: string;
  name: string;
  domain: string;
  /** Per-tenant DisplayName prefix configured in TenantConfig (Cosmos). Empty/undefined = none. */
  prefix?: string;
}

interface AuthorizedTenantsResponse {
  userId: string;
  username: string;
  authorizedTenants: string[];
  tenantInfo: Array<{
    tenantId: string;
    tenantName: string;
    role: string;
    prefix?: string;
  }>;
}

type LoadStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

// ---------- Adapter & initial state ----------
const tenantsAdapter = createEntityAdapter<Tenant, string>({
  // be explicit; some TS configs require this
  selectId: (t) => t.id,
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

const initialState = tenantsAdapter.getInitialState({
  status: 'idle' as LoadStatus,
  error: undefined as string | undefined,
});

// ---------- Thunk: fetch authorized tenants from API ----------
export const fetchTenants = createAsyncThunk<Tenant[]>(
  'tenants/fetchTenants',
  async (_, { rejectWithValue }) => {
    try {
      // Get access token using MSAL with configured scopes
      const token = await getAccessTokenForScope(appConfiguration.auth.scopes);
      
      if (!token) {
        return rejectWithValue('Failed to acquire access token');
      }

      // Call API with Bearer token in Authorization header
      const response = await fetch(`${appConfiguration.api.baseUrl}/Users/authorized-tenants`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: AuthorizedTenantsResponse = await response.json();
      
      // Extract tenantInfo array from response (contains both ID and name)
      const { tenantInfo } = data;
      
      if (!tenantInfo || !Array.isArray(tenantInfo)) {
        return rejectWithValue('Invalid tenant information received from API');
      }
      
      // Transform to Tenant[] format using actual tenant names
      const tenants: Tenant[] = tenantInfo.map(tenant => ({
        id: tenant.tenantId,
        name: tenant.tenantName,
        domain: "", // Empty for now
        prefix: tenant.prefix ?? ''
      }));
      
      return tenants;
      
    } catch (error: any) {
      // 5. Handle structured API errors
      let errorMessage = 'Failed to load authorized tenants';
      
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

// ---------- Slice ----------
const tenantsSlice = createSlice({
  name: 'tenants',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = undefined;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTenants.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
      })
      .addCase(fetchTenants.fulfilled, (state, action) => {
        state.status = 'succeeded';
        tenantsAdapter.setAll(state, action.payload);
      })
      .addCase(fetchTenants.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Failed to load tenants';
      });
  },
});

export const { clearError } = tenantsSlice.actions;
export default tenantsSlice.reducer;

// ---------- Base adapter selectors ----------
const adapterSelectors = tenantsAdapter.getSelectors<RootState>((s) => s.tenants);

export const selectAllTenants = adapterSelectors.selectAll;
export const selectTenantEntities = adapterSelectors.selectEntities;

// ---------- Derived selectors for UI ----------
export type TenantOption = { id: string; name: string };

export const selectTenantOptions = createSelector(selectAllTenants, (all): TenantOption[] =>
  all.map((t) => ({ id: t.id, name: t.name })),
);

export const selectTenantNameById = (state: RootState, id?: string | null): string => {
  if (!id) return '';
  return selectTenantEntities(state)[id]?.name ?? '';
};

/** Per-tenant DisplayName prefix (from TenantConfig via authorized-tenants). '' when none configured. */
export const selectTenantPrefix = (state: RootState, id?: string | null): string => {
  if (!id) return '';
  const entities = selectTenantEntities(state);
  // Tenant IDs are matched case-insensitively across the app; the entity map is keyed by the
  // id as returned from authorized-tenants, which may differ in case from the deployment tenantId.
  const exact = entities[id];
  if (exact) return exact.prefix ?? '';
  const lowerId = id.toLowerCase();
  const match = Object.values(entities).find((t) => t?.id?.toLowerCase() === lowerId);
  return match?.prefix ?? '';
};

export const selectTenantsStatus = (state: RootState) => state.tenants.status;
export const selectTenantsError = (state: RootState) => state.tenants.error;

// ---------- Tenant name-to-ID mapping selectors for dashboard entities ----------
export const selectTenantNameToIdMap = createSelector(selectAllTenants, (tenants) =>
  Object.fromEntries(tenants.map(t => [t.name, t.id]))
);

export const selectTenantIdsByNames = createSelector(
  [selectTenantNameToIdMap, (state: RootState, tenantNames: string[]) => tenantNames],
  (nameToIdMap, tenantNames) => 
    tenantNames.map(name => nameToIdMap[name]).filter(Boolean)
);
